import { DrawerBackdrop, DrawerContent, DrawerRoot } from '@/components/ui/drawer';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import ContactDrawerErrorState from './ContactDrawerErrorState';
import type { ContactShell } from './contact-types';
import ContactDetailPanel from './detail/ContactDetailPanel';
import ContactRail from './rail/ContactRail';
import { useContactProposeCollab } from './rail/use-contact-propose-collab';

interface ContactDrawerProps {
  contact: ContactShell;
  onClose: () => void;
  /**
   * Settled load failure (unresolvable contact / summary 5xx). When set, the
   * shell renders an explicit error body with a retry affordance instead of the
   * rail + detail columns. `null`/absent renders normally.
   */
  error?: { retry: () => void } | null;
}

/** Fixed width of the navigation rail on the desktop two-column layout. */
const RAIL_WIDTH = '322px';

/**
 * Two-column contact drawer shell.
 *
 * Desktop: a flex row inside DrawerContent (~908px) — a fixed 322px rail with
 * its own vertical scroll and a sticky identity header, beside a flex detail
 * panel with its own independent vertical scroll and a sticky context bar. No
 * shadow; the backdrop blur and the panel border carry the separation.
 *
 * Mobile (< md): collapses to a single column. The rail IS the drawer; choosing
 * a rail item pushes the detail in as a second screen, dismissable via the
 * context-bar back chevron.
 *
 * lazyMount + unmountOnExit reset the whole tree on close so reopening starts
 * clean.
 *
 * Closing plays the exit before `onClose` clears the URL. Clearing it on the
 * outside pointerdown unmounted the backdrop at once, and the click that
 * followed landed on whatever sat behind it.
 */
const ContactDrawer: React.FC<ContactDrawerProps> = ({ contact, onClose, error = null }) => {
  const isStacked = useBreakpointValue({ base: true, md: false }) ?? false;
  const [mobileScreen, setMobileScreen] = useState<'rail' | 'detail'>('rail');
  const [open, setOpen] = useState(true);
  const requestClose = useCallback(() => setOpen(false), []);
  const {
    onPropose,
    onProposeUgc,
    isProposing,
    modal: proposeCollabModal,
  } = useContactProposeCollab();

  const showRailScreen = !isStacked || mobileScreen === 'rail';
  const showDetailScreen = !isStacked || mobileScreen === 'detail';

  const handleMobilePush = useCallback(() => {
    if (isStacked) setMobileScreen('detail');
  }, [isStacked]);

  const handleMobileBack = useCallback(() => setMobileScreen('rail'), []);

  return (
    <DrawerRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) requestClose();
      }}
      onExitComplete={onClose}
      size="xl"
      unmountOnExit
      lazyMount
    >
      <DrawerBackdrop backdropFilter="blur(8px)" />
      <DrawerContent
        bg="panelBg"
        maxW="908px"
        width="100%"
        h="100dvh"
        display="flex"
        flexDirection="row"
        overflow="hidden"
        borderLeftWidth="1px"
        borderColor="border"
      >
        {error ? (
          <ContactDrawerErrorState onRetry={error.retry} onClose={requestClose} />
        ) : (
          <>
            {showRailScreen && (
              <Box
                as="aside"
                width={isStacked ? '100%' : RAIL_WIDTH}
                flex="none"
                h="100%"
                overflowY="auto"
                bg="sidebarBg"
                borderRightWidth={isStacked ? '0' : '1px'}
                borderColor="border"
              >
                <ContactRail
                  contact={contact}
                  onSelect={handleMobilePush}
                  onPropose={onPropose}
                  onProposeUgc={onProposeUgc}
                  isProposing={isProposing}
                />
              </Box>
            )}

            {showDetailScreen && (
              <ContactDetailPanel
                contact={contact}
                onClose={requestClose}
                forceBack={isStacked}
                onBack={isStacked ? handleMobileBack : undefined}
              />
            )}
          </>
        )}
        {/* Mounted at drawer level so a mobile rail→detail screen swap can't
            unmount the flow mid-step. */}
        {proposeCollabModal}
      </DrawerContent>
    </DrawerRoot>
  );
};

export default ContactDrawer;
